// Side drawer: full Gmail thread for an email-follow-up obligation row.
// Long message bodies show a sneak peek + Expand/Collapse; FAB scrolls to latest.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Loader2, AlertCircle, X, Mail, ArrowDownLeft, ArrowUpRight, Paperclip,
  ChevronDown, ChevronsDown, ChevronsUp,
} from "lucide-react";
import Drawer from "./Drawer";
import { Badge } from "../atoms";
import { useEmailThread } from "../hooks/useEmails";
import { getOutboundHeaderTitle } from "../utils/emailDisplayUtils";

const PEEK_MAX = 280;

function sanitizeEmailHtml(html) {
  if (!html) return "";
  let out = String(html);
  out = out.replace(/<script[\s\S]*?<\/script\s*>/gi, "");
  out = out.replace(/<(iframe|object|embed|link|meta)\b[^>]*>/gi, "");
  out = out.replace(/\s+on[a-z]+\s*=\s*"[^"]*"/gi, "");
  out = out.replace(/\s+on[a-z]+\s*=\s*'[^']*'/gi, "");
  out = out.replace(/\s+on[a-z]+\s*=\s*[^\s>]+/gi, "");
  out = out.replace(/(href|src|action)\s*=\s*"\s*javascript:[^"]*"/gi, '$1="#"');
  out = out.replace(/(href|src|action)\s*=\s*'\s*javascript:[^']*'/gi, "$1='#'");
  return out;
}

function HtmlBody({ html, onHeight }) {
  const hostRef = useRef(null);
  const sanitized = useMemo(() => sanitizeEmailHtml(html), [html]);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return undefined;
    let root = host.shadowRoot;
    if (!root) root = host.attachShadow({ mode: "open" });
    root.innerHTML = `
      <style>
        :host { display: block; width: 100%; }
        .crm-mail-root {
          all: initial;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif;
          color: inherit; font-size: 14px; line-height: 1.6;
          display: block; width: 100%; word-wrap: break-word; overflow-wrap: anywhere;
        }
        .crm-mail-root img, .crm-mail-root video { max-width: 100% !important; height: auto !important; }
        .crm-mail-root table { max-width: 100% !important; border-collapse: collapse; }
        .crm-mail-root a { color: inherit; text-decoration: underline; }
        .crm-mail-root blockquote {
          margin: 12px 0; padding: 4px 12px;
          border-left: 3px solid rgba(127,127,127,0.25); color: rgba(127,127,127,0.9);
        }
      </style>
      <div class="crm-mail-root">${sanitized}</div>
    `;
    const onClick = (e) => {
      const t = e.target.closest && e.target.closest("a");
      if (t && t.getAttribute("href")) {
        e.preventDefault();
        const href = t.getAttribute("href");
        if (href && href !== "#") window.open(href, "_blank", "noopener,noreferrer");
      }
    };
    root.addEventListener("click", onClick);
    const rootEl = root.querySelector(".crm-mail-root");
    let cancelled = false;
    const timeouts = [];
    function measure() {
      if (cancelled || !rootEl) return;
      const h = rootEl.scrollHeight || rootEl.offsetHeight || 0;
      if (h) onHeight?.(h);
    }
    measure();
    [50, 200, 500, 1000].forEach((d) => timeouts.push(setTimeout(measure, d)));
    let ro;
    try {
      ro = new ResizeObserver(measure);
      if (rootEl) ro.observe(rootEl);
    } catch (_) { /* ignore */ }
    if (rootEl) {
      rootEl.querySelectorAll("img").forEach((img) => {
        if (!img.complete) {
          img.addEventListener("load", measure, { once: true });
          img.addEventListener("error", measure, { once: true });
        }
      });
    }
    return () => {
      cancelled = true;
      timeouts.forEach(clearTimeout);
      ro?.disconnect();
      root.removeEventListener("click", onClick);
    };
  }, [sanitized, onHeight]);

  return <div ref={hostRef} />;
}

function fmtWhen(d) {
  if (!d) return "—";
  return new Date(d).toLocaleString(undefined, {
    month: "short", day: "numeric", year: "numeric",
    hour: "numeric", minute: "2-digit",
  });
}

const toggleBtnStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: 5,
  padding: "5px 10px",
  fontSize: 11,
  fontWeight: 600,
  background: "var(--color-bg-muted)",
  border: "1px solid var(--color-border-subtle)",
  borderRadius: 6,
  cursor: "pointer",
  color: "var(--color-text-secondary)",
};

function MessageCard({ msg, collapsed, onToggle }) {
  const outbound = msg.direction === "outbound";
  const tone = outbound ? "var(--crm-primary)" : "var(--crm-success)";
  const Icon = outbound ? ArrowUpRight : ArrowDownLeft;
  const title = outbound
    ? getOutboundHeaderTitle(msg)
    : (msg.fromName ? `${msg.fromName} <${msg.fromAddress || ""}>` : (msg.fromAddress || "Unknown"));
  const hasHtml = !!(msg.bodyHtml && String(msg.bodyHtml).trim());
  const [contentH, setContentH] = useState(0);
  const onHeight = useCallback((h) => {
    setContentH((prev) => (Math.abs(prev - h) > 2 ? h : prev));
  }, []);

  const textLen = (msg.bodyText || msg.snippet || "").length;
  const needsPeek = hasHtml
    ? contentH > PEEK_MAX + 24
    : textLen > 600 || contentH > PEEK_MAX;
  const isPeeking = collapsed && needsPeek;

  return (
    <div style={{
      border: "1px solid var(--color-border-subtle)",
      borderRadius: 12,
      background: "var(--color-bg-surface)",
      overflow: "hidden",
      flexShrink: 0,
    }}
    >
      <div style={{
        padding: "12px 14px",
        borderBottom: "1px solid var(--color-border-subtle)",
        display: "flex", gap: 10, alignItems: "flex-start",
      }}
      >
        <div style={{
          width: 28, height: 28, borderRadius: 8, flexShrink: 0,
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          background: "var(--color-bg-muted)", color: tone,
        }}
        >
          <Icon size={14} />
        </div>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: "var(--color-text-primary)" }}>
            {title}
          </div>
          <div style={{ fontSize: 11, color: "var(--color-text-secondary)", marginTop: 2 }}>
            {fmtWhen(msg.sentAt || msg.receivedAt)}
            {outbound ? " · Sent" : " · Received"}
          </div>
          {msg.subject && (
            <div style={{ fontSize: 12, color: "var(--color-text-secondary)", marginTop: 4 }}>
              {msg.subject}
            </div>
          )}
        </div>
        <Badge tone={outbound ? "primary" : "success"}>{outbound ? "Out" : "In"}</Badge>
      </div>

      <div style={{ padding: "14px 16px", position: "relative" }}>
        <div style={{
          maxHeight: isPeeking ? PEEK_MAX : "none",
          overflow: isPeeking ? "hidden" : "visible",
          position: "relative",
        }}
        >
          {hasHtml ? (
            <HtmlBody html={msg.bodyHtml} onHeight={onHeight} />
          ) : (
            <div
              ref={(el) => {
                if (el) onHeight(el.scrollHeight);
              }}
              style={{
                fontSize: 13.5, color: "var(--color-text-primary)",
                whiteSpace: "pre-wrap", wordBreak: "break-word", lineHeight: 1.6,
              }}
            >
              {msg.bodyText || msg.snippet || "(no body)"}
            </div>
          )}
          {isPeeking && (
            <div style={{
              pointerEvents: "none",
              position: "absolute",
              left: 0, right: 0, bottom: 0, height: 72,
              background: "linear-gradient(to bottom, transparent, var(--color-bg-surface))",
            }}
            />
          )}
        </div>

        {needsPeek && (
          <div style={{ marginTop: 10, display: "flex", justifyContent: "center" }}>
            <button type="button" onClick={onToggle} style={toggleBtnStyle}>
              {collapsed ? (
                <><ChevronDown size={12} /> Expand</>
              ) : (
                <><ChevronsUp size={12} /> Collapse</>
              )}
            </button>
          </div>
        )}

        {!!msg.attachments?.length && (
          <div style={{ marginTop: 14, paddingTop: 12, borderTop: "1px solid var(--color-border-subtle)" }}>
            <div style={{
              fontSize: 11, fontWeight: 700, textTransform: "uppercase",
              letterSpacing: "0.06em", color: "var(--color-text-secondary)",
              marginBottom: 8, display: "flex", alignItems: "center", gap: 5,
            }}
            >
              <Paperclip size={11} /> {msg.attachments.length} attachment{msg.attachments.length === 1 ? "" : "s"}
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {msg.attachments.map((att, i) => (
                <span
                  key={i}
                  style={{
                    display: "inline-flex", alignItems: "center", gap: 5,
                    padding: "5px 10px", borderRadius: 6,
                    border: "1px solid var(--color-border-subtle)",
                    background: "var(--color-bg-muted)",
                    fontSize: 12, color: "var(--color-text-secondary)",
                  }}
                >
                  <Paperclip size={11} /> {att.filename || "attachment"}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function FollowUpThreadDrawer({ open, onClose, item }) {
  const accountId = item?.accountId ? String(item.accountId) : null;
  const threadId = item?.providerThreadId || null;
  const lead = item?.lead || {};
  const scrollRef = useRef(null);
  const [collapsedMap, setCollapsedMap] = useState({});
  const [showJumpBottom, setShowJumpBottom] = useState(false);

  const { data, isLoading, isError, error } = useEmailThread(
    open ? accountId : null,
    open ? threadId : null,
  );

  const messages = useMemo(() => {
    const list = data?.messages || [];
    return [...list].sort((a, b) => {
      const ta = new Date(a.sentAt || a.receivedAt || 0).getTime();
      const tb = new Date(b.sentAt || b.receivedAt || 0).getTime();
      return ta - tb;
    });
  }, [data?.messages]);

  // Default every message to collapsed (sneak peek) when thread opens / changes
  useEffect(() => {
    if (!open) return;
    const next = {};
    messages.forEach((m) => {
      const id = m.providerMessageId || m.id;
      if (id) next[id] = true;
    });
    setCollapsedMap(next);
    setShowJumpBottom(false);
  }, [open, threadId, messages.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const subject = messages.find((m) => m.subject)?.subject
    || data?.subject
    || lead.title
    || "Email thread";

  const anyExpanded = messages.some((m) => {
    const id = m.providerMessageId || m.id;
    return id && collapsedMap[id] === false;
  });

  const expandAll = () => {
    const next = {};
    messages.forEach((m) => {
      const id = m.providerMessageId || m.id;
      if (id) next[id] = false;
    });
    setCollapsedMap(next);
  };

  const collapseAll = () => {
    const next = {};
    messages.forEach((m) => {
      const id = m.providerMessageId || m.id;
      if (id) next[id] = true;
    });
    setCollapsedMap(next);
  };

  const toggleOne = (id) => {
    setCollapsedMap((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const onScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const dist = el.scrollHeight - el.scrollTop - el.clientHeight;
    setShowJumpBottom(dist > 80);
  };

  useEffect(() => {
    if (!open || isLoading) return undefined;
    const t = setTimeout(onScroll, 120);
    return () => clearTimeout(t);
  }, [open, isLoading, messages.length, collapsedMap]);

  const scrollToBottom = () => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  };

  return (
    <Drawer open={open} onClose={onClose} width="min(640px, 92vw)">
      <div style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: 0,
        overflow: "hidden",
      }}
      >
        <div style={{
          flexShrink: 0, padding: "16px 18px",
          borderBottom: "1px solid var(--color-border-subtle)",
          display: "flex", alignItems: "flex-start", gap: 10,
        }}
        >
          <div style={{
            width: 36, height: 36, borderRadius: 10, flexShrink: 0,
            display: "flex", alignItems: "center", justifyContent: "center",
            background: "var(--crm-primary-soft)", color: "var(--crm-primary)",
          }}
          >
            <Mail size={18} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              fontSize: 15, fontWeight: 700, color: "var(--color-text-primary)",
              lineHeight: 1.35, overflow: "hidden", textOverflow: "ellipsis",
            }}
            >
              {subject}
            </div>
            <div style={{ fontSize: 12, color: "var(--color-text-secondary)", marginTop: 4 }}>
              {lead.title || "Lead"}
              {lead.email ? ` · ${lead.email}` : ""}
              {item?.ownerName ? ` · Owner: ${item.ownerName}` : ""}
              {messages.length ? ` · ${messages.length} msg${messages.length === 1 ? "" : "s"}` : ""}
            </div>
          </div>
          {messages.length > 0 && (
            <button
              type="button"
              onClick={anyExpanded ? collapseAll : expandAll}
              style={toggleBtnStyle}
              title={anyExpanded ? "Collapse all messages" : "Expand all messages"}
            >
              {anyExpanded ? (
                <><ChevronsUp size={12} /> Collapse</>
              ) : (
                <><ChevronsDown size={12} /> Expand</>
              )}
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{
              border: "1px solid var(--color-border-subtle)", background: "transparent",
              cursor: "pointer", borderRadius: 8, width: 32, height: 32,
              display: "inline-flex", alignItems: "center", justifyContent: "center",
              color: "var(--color-text-secondary)", flexShrink: 0,
            }}
          >
            <X size={15} />
          </button>
        </div>

        <div style={{ flex: 1, minHeight: 0, position: "relative" }}>
          <div
            ref={scrollRef}
            onScroll={onScroll}
            style={{
              position: "absolute",
              inset: 0,
              overflowY: "auto",
              overflowX: "hidden",
              WebkitOverflowScrolling: "touch",
              overscrollBehavior: "contain",
              padding: 16,
              paddingBottom: 56,
              display: "flex",
              flexDirection: "column",
              gap: 12,
              background: "var(--color-bg-muted)",
            }}
          >
            {!accountId || !threadId ? (
              <div style={{
                padding: 24, textAlign: "center", fontSize: 13,
                color: "var(--color-text-secondary)",
              }}
              >
                No Gmail thread linked to this follow-up row.
              </div>
            ) : null}

            {accountId && threadId && isLoading && (
              <div style={{
                display: "flex", alignItems: "center", justifyContent: "center",
                gap: 8, padding: 40, color: "var(--color-text-secondary)", fontSize: 13,
              }}
              >
                <Loader2 size={16} className="animate-spin" /> Loading email thread…
              </div>
            )}

            {isError && (
              <div style={{
                padding: 14, borderRadius: 10, background: "var(--crm-danger-soft)",
                color: "var(--crm-danger)", fontSize: 13,
              }}
              >
                <AlertCircle size={14} style={{ verticalAlign: "middle", marginRight: 6 }} />
                {error?.response?.data?.error || error?.message || "Failed to load thread"}
              </div>
            )}

            {!isLoading && !isError && accountId && threadId && messages.length === 0 && (
              <div style={{ padding: 24, textAlign: "center", fontSize: 13, color: "var(--color-text-secondary)" }}>
                Thread loaded but no messages found.
              </div>
            )}

            {messages.map((msg) => {
              const id = msg.providerMessageId || msg.id;
              return (
                <MessageCard
                  key={`${msg.accountId || accountId}:${id || msg.sentAt}`}
                  msg={msg}
                  collapsed={collapsedMap[id] !== false}
                  onToggle={() => toggleOne(id)}
                />
              );
            })}
          </div>

          {showJumpBottom && messages.length > 0 && (
            <button
              type="button"
              onClick={scrollToBottom}
              aria-label="Scroll to latest message"
              title="Jump to latest"
              style={{
                position: "absolute",
                right: 18,
                bottom: 16,
                zIndex: 2,
                width: 40,
                height: 40,
                borderRadius: 999,
                border: "1px solid var(--color-border-subtle)",
                background: "var(--color-bg-surface)",
                color: "var(--crm-primary)",
                boxShadow: "0 6px 18px rgba(0,0,0,0.12)",
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <ChevronsDown size={18} />
            </button>
          )}
        </div>
      </div>
    </Drawer>
  );
}
